import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { CourseDetailService } from '../services/courseDetail/course-detail.service';
import { UserService } from '../services/userService/user.service';
import { FavoritesService } from '../services/favoritesService/favorites.service';
import { VideoService } from '../services/VideoService/video.service';
import { GoogleAnalyticsService } from '../services/googleAnalytics/google-analytics.service';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-course-detail',
  templateUrl: './course-detail.component.html',
  styleUrls: ['./course-detail.component.scss'],
})
export class CourseDetailComponent implements OnInit, OnDestroy {
  course: any = null;
  open = false;
  playing = false;
  showMeta = false;
  menuOpen = false;
  userID: any;

  private sub: Subscription;

  constructor(
    private courseDetail: CourseDetailService,
    private authService: UserService,
    private favService: FavoritesService,
    private videoService: VideoService,
    private googleAnalytics: GoogleAnalyticsService,
    private toastController: ToastController,
    public translate: TranslateService
  ) {}

  get isWebinar(): boolean {
    return !!(this.course && this.course.__isWebinar);
  }

  // Firebase per-user view counter (course or webinar), lost when the old
  // detail pages were replaced by this drawer.
  private counterVideoView() {
    if (!this.userID) { this.userID = this.authService.getUserUid(); }
    const id = this.course?.key?.key;
    if (!this.userID || !id) { return; }
    const viewBoolean = { boolean: true };
    if (this.isWebinar) {
      this.videoService.updateWebinarForCounterVideo(id, this.userID, viewBoolean);
    } else {
      this.videoService.updateCourseForCounterVideo(id, this.userID, viewBoolean);
    }
  }

  ngOnInit() {
    this.userID = this.authService.getUserUid();
    this.sub = this.courseDetail.course$.subscribe((c) => {
      this.course = c;
      this.open = !!c;
      this.playing = false;
      this.showMeta = false;
      this.menuOpen = false;
      if (this.open) {
        document.body.classList.add('reader-open');
      } else {
        document.body.classList.remove('reader-open');
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) { this.sub.unsubscribe(); }
    document.body.classList.remove('reader-open');
  }

  get videoUrl(): string {
    const link = this.course?.link || '';
    if (!link) { return ''; }
    return link + (link.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';
  }

  get title(): string {
    return this.course?.titleCourse || this.course?.titleWebinar || '';
  }

  get metaSummary(): string {
    if (!this.course) { return ''; }
    const doctors = (this.course.docteur || []).map((d) => d.nom).filter(Boolean).join(', ');
    const product = (this.course.product && this.course.product.length) ? this.course.product[0].name : '';
    const parts = [doctors, product, this.course.language, this.course.duration]
      .filter((v) => v !== null && v !== undefined && v !== '');
    return parts.join('  ·  ');
  }

  get favOn(): boolean {
    return this.course?.isFav === 'true' || this.course?.isFav === true;
  }

  play() {
    this.playing = true;
    this.counterVideoView();
    this.googleAnalytics.trackVideoPlay(this.course, !this.isWebinar, this.course?.videoType);
  }

  toggleMeta() {
    this.showMeta = !this.showMeta;
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

  close() {
    document.body.classList.remove('reader-open');
    this.courseDetail.close();
  }

  toggleFav() {
    this.menuOpen = false;
    if (!this.userID) { this.userID = this.authService.getUserUid(); }
    const v = this.course;
    if (!v || !v.key || !v.key.key) { return; }
    if (this.favOn) {
      this.favService.removeFavVideos(this.userID, v.key.key);
      v.isFav = false;
      this.presentToast(this.translate.instant('messageFavorites.messageDelete'));
    } else {
      const courseData = {
        counterView: v.counterView ?? '0',
        creationDate: v.creationDate,
        docteur: v.docteur,
        duration: v.duration,
        language: v.language,
        link: v.link,
        product: v.product,
        bodyZone: v.bodyZone,
        needleType: v.needleType,
        titleCourse: v.titleCourse,
        urlImagesCourses: v.urlImageCourses,
        videoType: v.videoType,
      };
      this.favService.addVideosFav(this.userID, v.key.key, courseData);
      v.isFav = 'true';
      this.presentToast(this.translate.instant('messageFavorites.messageSuccess'));
    }
  }

  share() {
    this.menuOpen = false;
    const key = this.course?.key?.key || '';
    const slug = (this.title || '').replace(/\s+/g, '-');
    const url = window.location.origin + '/courses/' + slug + '/' + key;
    const nav: any = window.navigator;
    if (nav.share) {
      nav.share({ title: this.title, url }).catch(() => {});
    } else if (nav.clipboard) {
      nav.clipboard.writeText(url);
      this.presentToast(this.translate.instant('messageFavorites.messageSuccess') || 'Lien copié');
    }
  }

  async presentToast(text: string) {
    const toast = await this.toastController.create({
      message: text,
      duration: 2000,
      color: 'primary',
      position: 'top',
    });
    toast.present();
  }
}
